// The Feed strip: when this practice's authorizations last came in from a source, and the
// three ways to bring them in (staging walk item 8).
//
// No feed Atlas connects to supplies authorizations, so the path is a CSV of the EMR's
// authorization list: uploaded here by a person, or pushed daily by the auths-from-ecw
// Cowork skill. Either way the import is idempotent, and this strip says when it last
// happened.
//
//   * "Last refreshed" comes from the newest feed-owned authorization's updated_at, so an
//     import that changed nothing does not move it. Hand-entered auths never count.
//   * Upload is ALWAYS a dry run first. The biller sees the counts and every skipped row's
//     reason before an Import button exists, and Import sends the same file again.
//   * The skill text is copied from SKILL.md (skillText.js); the API key is never shown.
//
// Writes (upload) are admin, like every other write on this page; the template and the
// skill text are for anyone who can see the page.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { relativeTime } from "../queue/ageing";
import { API_URL, errorFrom } from "./AuthBits";
import {
  AUTHS_SKILL_EMRS,
  AUTHS_SKILL_TEXT_SOURCE,
  fillAuthsSkillText,
} from "./skillText";

const SOURCE_LABELS = {
  ecw_csv: "an eCW CSV",
  csv: "a CSV upload",
  cobalt: "eCW (Cobalt)",
  sheet: "the tracker sheet",
  olympus_sheet: "the tracker sheet",
};

const REASON_LABELS = {
  MISSING_AUTH_NUMBER: "no auth number",
  MISSING_PATIENT: "no patient columns filled",
  PATIENT_NOT_FOUND: "patient not found in Atlas",
  PATIENT_AMBIGUOUS: "more than one patient matched",
  PAYER_NOT_FOUND: "payer is not one of this practice's payers",
  PAYER_AMBIGUOUS: "more than one payer matched",
  MISSING_DATE: "an effective date is blank",
  BAD_DATE: "a date could not be read",
  BAD_DATE_RANGE: "ends before it starts",
  BAD_UNITS: "authorized units is not a whole number",
  BAD_VISITS_USED: "visits used is not a whole number",
  BAD_AUTH_TYPE: "auth type is not prior_auth or referral",
  DUPLICATE_IN_FILE: "same auth appears earlier in the file",
  SOURCE_ID_OTHER_PATIENT: "that auth is on file for a different patient",
  PATIENT_MISMATCH:
    "the account number's patient has a different name or date of birth",
  PATIENT_UNCONFIRMED: "an account number with no name or date of birth to confirm it",
  ALREADY_ON_FILE: "this patient already has that auth number on file",
  INVALID: "refused by the authorization rules",
};

export function sourceLabel(source) {
  return SOURCE_LABELS[source] || source || "a feed";
}

function Counts({ result }) {
  const skipped = result.skipped || [];
  return (
    <div className="auth-feed-counts">
      <p>
        {result.dry_run ? "Dry run — nothing was written. " : "Imported. "}
        {result.rows} row{result.rows === 1 ? "" : "s"}: {result.created} new,{" "}
        {result.updated} updated, {result.unchanged} unchanged, {skipped.length}{" "}
        skipped.
      </p>
      {skipped.length > 0 && (
        <ul className="auth-feed-skipped">
          {skipped.map((s) => (
            <li key={`${s.row}-${s.reason}`}>
              Row {s.row}: {REASON_LABELS[s.reason] || s.reason}
            </li>
          ))}
        </ul>
      )}
      {(result.ignored_columns || []).length > 0 && (
        <p className="auth-modal-note">
          Columns ignored: {result.ignored_columns.join(", ")}
        </p>
      )}
    </div>
  );
}

export default function FeedStrip({ practiceId, canWrite, onImported }) {
  const [feed, setFeed] = useState(null);
  const [feedFailed, setFeedFailed] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(null); // {file, result, busy, error}
  const [showSkill, setShowSkill] = useState(false);
  // Which EMR's section the dialog shows. Chosen by the reader, not inferred from
  // the practice's systems — pages read features, never emr_system.
  const [skillEmr, setSkillEmr] = useState("ecw");
  const [copyState, setCopyState] = useState(null);
  const fileInput = useRef(null);
  const preRef = useRef(null);
  const ticket = useRef(0);

  const loadFeed = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/authorizations/feed`,
        { credentials: "include" },
      );
      const payload = await response.json().catch(() => null);
      if (mine !== ticket.current) return;
      if (!response.ok || !payload || !("last_refreshed_at" in payload)) {
        setFeed(null);
        setFeedFailed(true);
        return;
      }
      setFeed(payload);
      setFeedFailed(false);
    } catch (err) {
      if (mine !== ticket.current) return;
      setFeed(null);
      setFeedFailed(true);
    }
  }, [practiceId]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const send = useCallback(
    async (file, dryRun) => {
      const form = new FormData();
      form.append("file", file);
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/authorizations/import${dryRun ? "?dry_run=1" : ""}`,
        { method: "POST", credentials: "include", body: form },
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload || typeof payload.rows !== "number") {
        throw new Error(errorFrom(response, payload, "The import did not run"));
      }
      return payload;
    },
    [practiceId],
  );

  const pick = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    setMessage(null);
    setUploading({ file, result: null, busy: true, error: null });
    try {
      const result = await send(file, true);
      setUploading({ file, result, busy: false, error: null });
    } catch (err) {
      setUploading({ file, result: null, busy: false, error: err.message });
    }
  };

  const confirmImport = async () => {
    if (!uploading || uploading.busy) return;
    const { file } = uploading;
    setUploading((u) => ({ ...u, busy: true, error: null }));
    try {
      const result = await send(file, false);
      setUploading(null);
      setMessage({ kind: "success", result });
      loadFeed();
      if (onImported) onImported();
    } catch (err) {
      setUploading((u) => ({ ...u, busy: false, error: err.message }));
    }
  };

  const downloadTemplate = async () => {
    setMessage(null);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/authorizations/import/template.csv`,
        { credentials: "include" },
      );
      if (!response.ok)
        throw new Error(`Could not download the template (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "authorizations-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setMessage({ kind: "error", text: err.message });
    }
  };

  const skillText = fillAuthsSkillText({
    practiceId,
    baseUrl: API_URL || window.location.origin,
    emr: skillEmr,
  });
  const skillEmrLabel =
    (AUTHS_SKILL_EMRS.find((e) => e.key === skillEmr) || AUTHS_SKILL_EMRS[0]).label;

  const copy = async () => {
    setCopyState(null);
    try {
      await navigator.clipboard.writeText(skillText);
      setCopyState("copied");
    } catch (err) {
      if (preRef.current) {
        const range = document.createRange();
        range.selectNodeContents(preRef.current);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setCopyState("failed");
    }
  };

  let status;
  if (feedFailed) {
    status = (
      <span className="auth-feed-status auth-feed-failed">
        Could not check when authorizations were last refreshed.
      </span>
    );
  } else if (!feed) {
    status = (
      <span className="auth-feed-status auth-muted">Checking the feed…</span>
    );
  } else if (!feed.last_refreshed_at) {
    status = (
      <span className="auth-feed-status">
        Auths never refreshed from a source — entered by hand.
      </span>
    );
  } else {
    status = (
      <span className="auth-feed-status">
        Auths last refreshed{" "}
        <strong>{relativeTime(feed.last_refreshed_at)}</strong> from{" "}
        {sourceLabel(feed.source_system)}.
      </span>
    );
  }
  // The timestamp moves only when an import CHANGES something, so a clean daily run would
  // otherwise look stale. The last run's counts are known only to the web process that ran it.
  const lastRun = feed && !feedFailed ? feed.rows_last_run : null;

  return (
    <div className="auth-feed" data-testid="auth-feed">
      <div className="auth-feed-row">
        <span>
          {status}
          {lastRun && (
            <span className="auth-feed-status auth-muted">
              {" "}
              Last import ran {relativeTime(lastRun.at)}: {lastRun.rows} rows,{" "}
              {lastRun.created} new, {lastRun.updated} updated,{" "}
              {lastRun.unchanged} unchanged, {lastRun.skipped} skipped.
            </span>
          )}
        </span>
        <span className="auth-feed-actions">
          {canWrite && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="auth-feed-file"
                onChange={pick}
                aria-label="Authorizations CSV"
              />
              <button
                type="button"
                className="auth-btn"
                onClick={() => fileInput.current && fileInput.current.click()}
              >
                Upload CSV
              </button>
            </>
          )}
          <button type="button" className="auth-btn" onClick={downloadTemplate}>
            Download CSV template
          </button>
          <button
            type="button"
            className="auth-btn"
            onClick={() => {
              setCopyState(null);
              setShowSkill(true);
            }}
          >
            Daily Cowork task
          </button>
        </span>
      </div>

      {message && (
        <div
          className={`auth-message auth-message-${message.kind} auth-message-inline`}
        >
          {message.result ? (
            <Counts result={message.result} />
          ) : (
            <span>{message.text}</span>
          )}
          <button
            type="button"
            className="auth-message-close"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {uploading && (
        <div
          className="auth-modal-overlay"
          onClick={uploading.busy ? undefined : () => setUploading(null)}
        >
          <div
            className="auth-modal auth-modal-small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="auth-modal-header">
              <h3>Import authorizations</h3>
              <button
                type="button"
                className="auth-modal-close"
                onClick={() => setUploading(null)}
                disabled={uploading.busy}
              >
                ×
              </button>
            </div>
            <div className="auth-modal-body">
              {uploading.error && (
                <div className="auth-message auth-message-error auth-message-inline">
                  {uploading.error}
                </div>
              )}
              {uploading.busy && !uploading.result && <p>Checking the file…</p>}
              {uploading.result && <Counts result={uploading.result} />}
              {uploading.result && (
                <p className="auth-modal-note">
                  Import sends this same file again and writes it. Re-importing
                  an unchanged file changes nothing.
                </p>
              )}
            </div>
            <div className="auth-modal-footer">
              <button
                type="button"
                className="auth-btn"
                onClick={() => setUploading(null)}
                disabled={uploading.busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="auth-btn auth-btn-primary"
                onClick={confirmImport}
                disabled={
                  uploading.busy ||
                  !uploading.result ||
                  uploading.result.created + uploading.result.updated === 0
                }
              >
                {uploading.busy && uploading.result ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSkill && (
        <div className="auth-modal-overlay" onClick={() => setShowSkill(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal-header">
              <h3>Daily Cowork task</h3>
              <button
                type="button"
                className="auth-modal-close"
                onClick={() => setShowSkill(false)}
              >
                ×
              </button>
            </div>
            <div className="auth-modal-body">
              <div className="auth-skill-emrs" role="group" aria-label="EMR">
                {AUTHS_SKILL_EMRS.map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    className={`auth-btn${skillEmr === e.key ? " auth-btn-primary" : ""}`}
                    aria-pressed={skillEmr === e.key}
                    onClick={() => {
                      setSkillEmr(e.key);
                      setCopyState(null);
                    }}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
              <p>
                Paste this into Cowork on the machine whose Chrome is logged in
                to {skillEmrLabel}. It runs against practice{" "}
                <strong>{practiceId}</strong>.
              </p>
              <div className="auth-fed-note">
                The skill needs an <code>auths:import</code> API key for this
                practice in <code>ATLAS_API_KEY</code>. An Atlas admin issues it
                on <strong>Practice Details → API keys</strong>, where it is
                shown once. The key goes into the environment on that machine —
                never into chat, a command line, or this page.
              </div>
              <pre className="auth-skill-text" ref={preRef}>
                {skillText}
              </pre>
              <p className="auth-modal-note">
                Text from {AUTHS_SKILL_TEXT_SOURCE.repo}{" "}
                {AUTHS_SKILL_TEXT_SOURCE.path} (“
                {AUTHS_SKILL_TEXT_SOURCE.sections[skillEmr]}”) at{" "}
                {AUTHS_SKILL_TEXT_SOURCE.commit}.
              </p>
            </div>
            <div className="auth-modal-footer">
              {copyState === "copied" && (
                <span className="auth-muted">Copied.</span>
              )}
              {copyState === "failed" && (
                <span className="auth-muted">
                  Copy was blocked — the text is selected; copy it with ⌘C or
                  Ctrl+C.
                </span>
              )}
              <button
                type="button"
                className="auth-btn auth-btn-primary"
                onClick={copy}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
